import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';

export interface RedirectionParams {
  l: string[] | null;
  q: Params | null;
  f: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class RedirectionService {
  constructor() {}

  // Resolves where a completed flow (login, registration, contact details)
  // should send the user next: the auth guard's redirectUrl takes priority
  // over the page-authored r param, falling back to the caller's own
  // default navigation when neither is present.
  async redirectAfterFlow(
    router: Router,
    redirectUrl: string | null | undefined,
    rParam: string | null | undefined,
    fallback: () => unknown,
  ): Promise<void> {
    if (redirectUrl) {
      await router.navigateByUrl(redirectUrl);
      return;
    }

    const redirectParams = this.getParams(rParam);
    if (redirectParams?.l) {
      await router.navigate(redirectParams.l, { queryParams: redirectParams.q, fragment: redirectParams.f ?? undefined });
      return;
    }

    await fallback();
  }

  getParams(url: string | null | undefined): RedirectionParams | null {
    if (!url) {
      return null;
    }
    return {
      l: this.getPathSegments(url),
      q: this.getQueryParams(url),
      f: this.getFragment(url),
    };
  }

  private getPathSegments(url: string): string[] | null {
    const matches = url.match(/^\/([^\?\#]*)/i);
    if (matches && matches[1]) {
      const segments = matches[1].split('/');
      if (segments.length) {
        segments[0] = `/${segments[0]}`;
      }
      return segments;
    }
    return null;
  }

  private getQueryParams(url: string): Params | null {
    const matches = url.match(/\?([^#]*)/i);
    if (matches && matches[1]) {
      const queryParams: Params = {};
      matches[1].split('&').forEach((q) => {
        const param = q.split('=');
        const key = decodeURIComponent(param[0]);
        const value = decodeURIComponent(param[1]);
        queryParams[key] = value;
      });
      return queryParams;
    }
    return null;
  }

  private getFragment(url: string): string | null {
    const matches = url.match(/#(.*)$/i);
    if (matches && matches[1]) {
      return decodeURIComponent(matches[1]);
    }
    return null;
  }
}
